import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PlugZap } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ResourcePage from "@/components/common/ResourcePage";
import { PageHeader } from "@/components/common/States";
import { JobsList } from "@/components/research/JobsList";
import { FindingsList } from "@/components/research/Findings";
import { useFetch } from "@/lib/hooks";
import { useWorkspace } from "@/context/WorkspaceContext";
import { campaigns, claims, evidence, projects, signals, sources } from "@/lib/researchResources";
import { errorMessage } from "@/lib/api";

export function useResearchActions() {
  const { api, can } = useWorkspace();
  const [busy, setBusy] = useState(null);
  const call = async (fn, ok) => { try { const r = await fn(); ok(r); } catch (e) { toast.error(errorMessage(e)); } };
  const evidenceActions = (row, reload) => (can("write") ? [
    ...(row.verification_status !== "verified" ? [{ id: "verify", label: "Mark verified (human review)", onClick: () => call(() => api.post(`/evidence/${row.id}/review`, { decision: "verified" }), () => { toast.success("Evidence marked verified"); reload(); }) }] : []),
    ...(row.verification_status !== "rejected" ? [{ id: "reject", label: "Reject evidence", onClick: () => call(() => api.post(`/evidence/${row.id}/review`, { decision: "rejected" }), () => { toast.success("Evidence rejected"); reload(); }) }] : []),
  ] : []);
  const claimActions = (row, reload) => (can("write") ? [{
    id: "ai-verify", label: busy === row.id ? "Verifying…" : "Run AI verification",
    onClick: () => { setBusy(row.id); toast.info("AI verification running — this can take up to a minute");
      call(() => api.post(`/claims/${row.id}/verify`), ({ data }) => {
        toast.success(`Outcome: ${data.verification.outcome} → status ${data.status}`, { description: data.verification.rationale.slice(0, 240) });
        reload();
      }).finally(() => setBusy(null)); },
  }] : []);
  return { evidenceActions, claimActions };
}

function ConnectorBanner() {
  const { data } = useFetch("/integrations");
  const sources = (data || []).filter((i) => i.category === "Data sources");
  const on = sources.filter((i) => i.status === "connected");
  if (!data) return null;
  return (
    <div className="mb-6 flex gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm" data-testid="connectors-banner">
      <PlugZap className="h-4 w-4 shrink-0 text-primary" />
      <p className="text-muted-foreground"><span className="font-medium text-foreground">{on.length} of {sources.length} data sources connected{on.length ? `: ${on.map((i) => i.name).join(", ")}` : ""}.</span> Tender modes use official procurement notices; web modes (Strategic Accounts, Partners &amp; competitors) use Tavily search on public pages only. Every excerpt must appear verbatim in the retrieved source, claims are challenged by a second AI check, and web findings are never Confirmed Procurement.</p>
    </div>
  );
}

export default function Research() {
  const navigate = useNavigate();
  const { evidenceActions, claimActions } = useResearchActions();
  const tabs = [
    ["campaigns", "Campaigns", <ResourcePage config={campaigns} onRowClick={(r) => navigate(`/research/campaigns/${r.id}`)} />],
    ["findings", "Findings", <FindingsList />],
    ["jobs", "Jobs", <JobsList />],
    ["evidence", "Evidence", <ResourcePage config={evidence} extraRowActions={evidenceActions} />],
    ["claims", "Claims", <ResourcePage config={claims} extraRowActions={claimActions} />],
    ["sources", "Sources", <ResourcePage config={sources} />],
    ["projects", "Projects", <ResourcePage config={projects} />],
    ["signals", "Buying signals", <ResourcePage config={signals} />],
  ];
  return (
    <div data-testid="research-page">
      <PageHeader eyebrow="AI research" title="Research Campaigns"
        description="Configure evidence-first research, monitor persistent jobs, and review sources, evidence and AI-verified claims." />
      <ConnectorBanner />
      <Tabs defaultValue="campaigns">
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
          {tabs.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`research-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([v, , el]) => <TabsContent key={v} value={v}>{el}</TabsContent>)}
      </Tabs>
    </div>
  );
}
